import { postNotify, routePostNotify } from 'services/ApiNotificationService';
import { isBrowser } from 'utils';
import Button, { SizeType, AppearanceTypes } from 'components/Shared/Button';
import { Button as ButtonLoader } from 'components/Shared/Loader';
import { Ref, AnchorHTMLAttributes } from 'react';

const clickNotificationType = 'click';
const addToCartNotificationType = 'adding_to_cart';

export const notify =
  (ticket: string, onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void | Promise<unknown>) =>
  (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (ticket) {
      if (isBrowser() && 'sendBeacon' in window.navigator) {
        window.navigator.sendBeacon(routePostNotify(ticket, clickNotificationType));
      } else {
        void postNotify(ticket, clickNotificationType);
      }
    }
    return onClick ? onClick(e) : undefined;
  };

export const notifyAddToCart = (ticket: string) => {
  void postNotify(ticket, addToCartNotificationType);
};

export interface NotifyProps {
  ticket: string;
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  setRef?: ((ref: HTMLAnchorElement) => void) | Ref<HTMLAnchorElement>;
}

interface LinkProps extends NotifyProps, AnchorHTMLAttributes<HTMLAnchorElement> {
  url: string;
  onMouseEnter?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  onMouseLeave?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  isDisabled?: boolean;
}

export const NotifyLink = ({
  ticket,
  url,
  className,
  style,
  children,
  onClick,
  setRef,
  onMouseEnter,
  onMouseLeave,
  isDisabled = false,
  ...attributes
}: LinkProps) => {
  if (isDisabled) {
    return <>{children}</>;
  }

  return (
    <a
      href={url}
      className={className}
      style={style}
      onClick={notify(ticket, onClick)}
      ref={setRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      {...attributes}
    >
      {children}
    </a>
  );
};

export interface NotifyButtonProps extends NotifyProps {
  size?: SizeType;
  url?: string;
  appearance?: AppearanceTypes;
}

export interface NotifyLoaderButtonProps extends NotifyButtonProps {
  loading?: boolean;
}

export const NotifyLoadingButton = ({
  ticket,
  url,
  className,
  style,
  size,
  children,
  onClick,
  appearance,
  loading
}: NotifyLoaderButtonProps) => (
  <ButtonLoader
    appearance={appearance}
    href={url}
    className={className}
    style={style}
    size={size}
    onClick={notify(ticket, onClick)}
    loading={loading}
  >
    {children}
  </ButtonLoader>
);

export const NotifyButton = ({
  ticket,
  url,
  className,
  style,
  size,
  children,
  onClick,
  appearance
}: NotifyButtonProps) => (
  <Button
    appearance={appearance}
    href={url}
    className={className}
    style={style}
    size={size}
    onClick={notify(ticket, onClick)}
  >
    {children}
  </Button>
);
